/**
 * A country entry rendered in the phone country selector.
 */
export interface PhoneCountry {
  /** ISO 3166-1 alpha-2 code, e.g. `IS`. */
  iso2: string;
  /** International calling code without the leading plus, e.g. `354`. */
  dialCode: string;
  /** Country name in the active interface language. */
  name: string;
  /** English name, matched by the search next to the localised one. */
  englishName: string;
  /** Flag as a Unicode regional indicator pair. */
  flag: string;
}

/**
 * Static country data. Only the countries a tenant actually offers are listed —
 * the picker is a shortcut for the common cases, not the country whitelist. That
 * one lives on the messaging provider; a number outside the list is still typed
 * by hand through the international mode.
 */
interface CountryEntry {
  dialCode: string;
  en: string;
  /**
   * Icelandic name. Chrome ships no `is` region data, so `Intl.DisplayNames` would
   * silently answer in English for the tenant whose main language this is.
   */
  is: string;
}

const COUNTRIES: Record<string, CountryEntry> = {
  IS: { dialCode: '354', en: 'Iceland', is: 'Ísland' },
  PL: { dialCode: '48', en: 'Poland', is: 'Pólland' },
  LT: { dialCode: '370', en: 'Lithuania', is: 'Litháen' },
  LV: { dialCode: '371', en: 'Latvia', is: 'Lettland' },
  RO: { dialCode: '40', en: 'Romania', is: 'Rúmenía' },
  GB: { dialCode: '44', en: 'United Kingdom', is: 'Bretland' },
  US: { dialCode: '1', en: 'United States', is: 'Bandaríkin' },
  DE: { dialCode: '49', en: 'Germany', is: 'Þýskaland' },
  DK: { dialCode: '45', en: 'Denmark', is: 'Danmörk' },
  AT: { dialCode: '43', en: 'Austria', is: 'Austurríki' },
  BE: { dialCode: '32', en: 'Belgium', is: 'Belgía' },
  BG: { dialCode: '359', en: 'Bulgaria', is: 'Búlgaría' },
  CA: { dialCode: '1', en: 'Canada', is: 'Kanada' },
  HR: { dialCode: '385', en: 'Croatia', is: 'Króatía' },
  CY: { dialCode: '357', en: 'Cyprus', is: 'Kýpur' },
  CZ: { dialCode: '420', en: 'Czechia', is: 'Tékkland' },
  EE: { dialCode: '372', en: 'Estonia', is: 'Eistland' },
  FO: { dialCode: '298', en: 'Faroe Islands', is: 'Færeyjar' },
  FI: { dialCode: '358', en: 'Finland', is: 'Finnland' },
  FR: { dialCode: '33', en: 'France', is: 'Frakkland' },
  GR: { dialCode: '30', en: 'Greece', is: 'Grikkland' },
  HU: { dialCode: '36', en: 'Hungary', is: 'Ungverjaland' },
  IE: { dialCode: '353', en: 'Ireland', is: 'Írland' },
  IT: { dialCode: '39', en: 'Italy', is: 'Ítalía' },
  LU: { dialCode: '352', en: 'Luxembourg', is: 'Lúxemborg' },
  MT: { dialCode: '356', en: 'Malta', is: 'Malta' },
  NL: { dialCode: '31', en: 'Netherlands', is: 'Holland' },
  NO: { dialCode: '47', en: 'Norway', is: 'Noregur' },
  PT: { dialCode: '351', en: 'Portugal', is: 'Portúgal' },
  RS: { dialCode: '381', en: 'Serbia', is: 'Serbía' },
  SK: { dialCode: '421', en: 'Slovakia', is: 'Slóvakía' },
  SI: { dialCode: '386', en: 'Slovenia', is: 'Slóvenía' },
  ES: { dialCode: '34', en: 'Spain', is: 'Spánn' },
  SE: { dialCode: '46', en: 'Sweden', is: 'Svíþjóð' },
  CH: { dialCode: '41', en: 'Switzerland', is: 'Sviss' },
  UA: { dialCode: '380', en: 'Ukraine', is: 'Úkraína' },
  GA: { dialCode: '241', en: 'Gabon', is: 'Gabon' },
};

/**
 * The countries one tenant offers.
 */
export interface PhoneCountriesConfig {
  /**
   * Shown above the divider in exactly this order — the countries the tenant's
   * customers pick most often. The first one is the default selection.
   */
  pinned: string[];
  /** Shown below the divider, sorted alphabetically in the active language. */
  rest: string[];
}

/** EEA plus the UK, US/Canada, Ukraine and Serbia — diasporas and tourist markets. */
export const HEIMKAUP_PHONE_COUNTRIES: PhoneCountriesConfig = {
  pinned: ['IS', 'PL', 'LT', 'LV', 'RO', 'GB', 'US', 'DE', 'DK'],
  rest: [
    'AT',
    'BE',
    'BG',
    'CA',
    'HR',
    'CY',
    'CZ',
    'EE',
    'FO',
    'FI',
    'FR',
    'GR',
    'HU',
    'IE',
    'IT',
    'LU',
    'MT',
    'NL',
    'NO',
    'PT',
    'RS',
    'SK',
    'SI',
    'ES',
    'SE',
    'CH',
    'UA',
  ],
};

/** Gabon only — a single entry renders as a static prefix, not as a dropdown. */
export const CERISE_PHONE_COUNTRIES: PhoneCountriesConfig = {
  pinned: ['GA'],
  rest: [],
};

/** The country list of one tenant, resolved for one interface language. */
export interface PhoneCountryList {
  pinned: PhoneCountry[];
  rest: PhoneCountry[];
  /** Both groups in display order, which is also the resolution order. */
  all: PhoneCountry[];
}

/**
 * Turns an ISO 3166-1 alpha-2 code into its flag, e.g. `IS` into 🇮🇸. Regional
 * indicator symbols sit 0x1F1A5 above the ASCII letters.
 */
export const flagEmoji = (iso2: string): string =>
  String.fromCodePoint(
    ...Array.from(
      iso2.toUpperCase(),
      (letter) => letter.charCodeAt(0) + 0x1f1a5,
    ),
  );

/**
 * Region display names for the active language, always with an explicit English
 * fallback.
 *
 * `Intl.DisplayNames(['is'])` does not throw when Chrome ships no region names for
 * Icelandic — it silently resolves to the *browser's* locale, so an Icelandic
 * shopper on a Russian-locale browser would read Russian country names.
 */
const createDisplayNames = (locale: string): Intl.DisplayNames | null => {
  try {
    return new Intl.DisplayNames([locale, 'en'], { type: 'region' });
  } catch {
    return null;
  }
};

const buildCountry = (
  iso2: string,
  locale: string,
  displayNames: Intl.DisplayNames | null,
): PhoneCountry | null => {
  const entry = COUNTRIES[iso2];

  if (!entry) {
    return null;
  }

  const localised = displayNames?.of(iso2);

  return {
    iso2,
    dialCode: entry.dialCode,
    name: locale.toLowerCase().startsWith('is')
      ? entry.is
      : localised && localised !== iso2
        ? localised
        : entry.en,
    englishName: entry.en,
    flag: flagEmoji(iso2),
  };
};

const isPhoneCountry = (
  country: PhoneCountry | null,
): country is PhoneCountry => country !== null;

/**
 * The Icelandic alphabet, in order. C, Q, W and Z only turn up in loanwords and
 * take their Latin places.
 *
 * Chrome ships no collation for `is` — `Intl.Collator.supportedLocalesOf(['is'])`
 * comes back empty and `localeCompare(…, 'is')` quietly resolves to the *browser's*
 * locale instead. The list then came out in a different order for every shopper,
 * and never in Icelandic: `Færeyjar` landed among the F's rather than after `Þ`.
 */
const ICELANDIC_ALPHABET = 'AÁBCDÐEÉFGHIÍJKLMNOÓPQRSTUÚVWXYÝZÞÆÖ';

/** Maps a name onto a string whose code points follow the Icelandic order. */
const icelandicSortKey = (name: string): string =>
  Array.from(name.toUpperCase(), (letter) => {
    const rank = ICELANDIC_ALPHABET.indexOf(letter);

    return rank < 0 ? letter : String.fromCharCode(0x41 + rank);
  }).join('');

/**
 * Sorts by the localised name. Icelandic gets the explicit alphabet above; every
 * other language goes through `Intl` with a spelled-out English fallback, so a
 * locale it has no data for lands somewhere predictable rather than wherever the
 * browser happens to be set.
 */
const sortByName = (
  countries: PhoneCountry[],
  locale: string,
): PhoneCountry[] => {
  if (locale.toLowerCase().startsWith('is')) {
    return countries
      .map((country) => ({ country, key: icelandicSortKey(country.name) }))
      .sort((a, b) => (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
      .map(({ country }) => country);
  }

  return [...countries].sort((a, b) =>
    a.name.localeCompare(b.name, [locale, 'en']),
  );
};

/**
 * Resolves a tenant's country list for one interface language: the pinned block
 * keeps its configured order, everything else is sorted by the localised name.
 */
export const buildPhoneCountries = (
  config: PhoneCountriesConfig,
  locale: string,
): PhoneCountryList => {
  const displayNames = createDisplayNames(locale);
  const build = (codes: string[]) =>
    codes
      .map((iso2) => buildCountry(iso2, locale, displayNames))
      .filter(isPhoneCountry);

  const pinned = build(config.pinned);
  const rest = sortByName(build(config.rest), locale);

  return { pinned, rest, all: [...pinned, ...rest] };
};

/**
 * Picks the country a full international number belongs to, preferring the longest
 * matching calling code. Countries sharing a code — `+1` for the US and Canada —
 * are settled by list order, which puts the pinned ones first.
 */
export const resolveCountryFromValue = (
  value: string,
  countries: PhoneCountry[],
): PhoneCountry | undefined => {
  const digits = value.replace(/\D/g, '');

  if (!digits) {
    return undefined;
  }

  let best: PhoneCountry | undefined;

  for (const country of countries) {
    if (!digits.startsWith(country.dialCode)) {
      continue;
    }

    if (!best || country.dialCode.length > best.dialCode.length) {
      best = country;
    }
  }

  return best;
};

/**
 * Returns the national part of a full international number, i.e. everything after
 * the calling code of the country it resolves to. Falls back to every digit when no
 * country matches, which is what test numbers such as `+0…` hit.
 */
export const splitNationalPart = (
  value: string,
  countries: PhoneCountry[],
): string => {
  const digits = value.replace(/\D/g, '');
  const country = resolveCountryFromValue(value, countries);

  return country ? digits.slice(country.dialCode.length) : digits;
};

/**
 * Strips case and diacritics so that a search reaches names the shopper cannot
 * type: `polland` finds `Pólland`.
 */
const normalise = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

/**
 * Filters countries by localised name, English name, ISO code or calling code. The
 * English name is matched in every language on purpose — a Pole shopping on the
 * Icelandic site searches for "Poland", not for "Pólland".
 */
export const filterCountries = (
  countries: PhoneCountry[],
  query: string,
): PhoneCountry[] => {
  const normalised = normalise(query);

  if (!normalised) {
    return countries;
  }

  const digits = normalised.replace(/\D/g, '');

  return countries.filter(
    (country) =>
      normalise(country.name).includes(normalised) ||
      normalise(country.englishName).includes(normalised) ||
      country.iso2.toLowerCase().includes(normalised) ||
      (!!digits && country.dialCode.startsWith(digits)),
  );
};
